import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import { Container, Row, Col, Button, Form, Modal, Image, Dropdown, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import Header from '../layout/Header';
import SendGuideModal from '../common/SendGuideModal';
import { useDebounce } from '../../../hooks/useDebounce';
import { 
  JOB_TYPE_OPTIONS, 
  POSITION_OPTIONS, 
  STATUS, 
  STATUS_TEXT, 
  STATUS_CLASS,
  PAGINATION,
  StatusType,
  getJobTypeString,
  getPositionString
} from '../../../utils/Constants';
import { 
  getSortIcon, 
  renderPagination,
  validateEmail,
  containsEmoji
} from '../../../utils/Utils';
import './CandidateList.css';
import { Candidate, CandidateFormData, User, ValidationErrors, PaginationData, QuestionTemplate, SortConfig } from '../../../types';
import { ApiError } from '../../../types/errors';
import QuestionTemplateSelectionModal from '../common/QuestionTemplateSelectionModal';


const CandidateList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [candidates, setCandidates] = useState<PaginationData>({} as PaginationData);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [editedCandidate, setEditedCandidate] = useState<Candidate | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedCandidates, setSelectedCandidates] = useState<number[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [user, setUser] = useState<User | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [questionTemplates, setQuestionTemplates] = useState<QuestionTemplate[]>([]);
  const [showQuestionTemplateModal, setShowQuestionTemplateModal] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch (error: unknown) {
        console.error('Error parsing user data:', error);
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  const fetchQuestionTemplates = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/question-templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionTemplates(response.data);
    } catch (error: unknown) {
      console.error('Error fetching question templates:', error);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [currentPage, sortConfig, debouncedSearchTerm, activeTab]);

  useEffect(() => {
    fetchQuestionTemplates();
  }, []);

  const fetchCandidates = async (): Promise<void> => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(activeTab !== 'all' && { status: getStatusValue(activeTab).toString() })
      });

      const response = await axios.get(`/candidates?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(response.data);
      setLoading(false);
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error fetching candidates:', error);
      setCandidates({} as PaginationData);
      setLoading(false);
    }
  };

  const getStatusValue = (tab: string): number => {
    switch (tab) {
      case 'pending': return STATUS.PENDING;
      case 'in-progress': return STATUS.IN_PROGRESS;
      case 'completed': return STATUS.COMPLETED;
      default: return 0;
    }
  };


  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleTabChange = (tab: 'all' | 'pending' | 'in-progress' | 'completed'): void => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedCandidates([]);
  };

  const handleSelectCandidate = (id: number): void => {
    if (selectedCandidates.includes(id)) {
      setSelectedCandidates(selectedCandidates.filter(candidateId => candidateId !== id));
    } else {
      setSelectedCandidates([...selectedCandidates, id]);
    }
  };

  const handleSelectAllCandidates = (): void => {
    const currentCandidates = candidates.data || [];
    if (selectedCandidates.length === currentCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(currentCandidates.map(candidate => candidate.id));
    }
  };

  const viewCandidateDetails = (candidate: Candidate): void => {
    setValidationErrors({});
    setSelectedCandidate(candidate);
    setEditedCandidate(null);
    setIsEditMode(false);
    setShowDetailModal(true);
  };

  const handleEditMode = (): void => {
    if (selectedCandidate) {
      setEditedCandidate({...selectedCandidate});
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = (): void => {
    setIsEditMode(false);
    setEditedCandidate(null);
    setValidationErrors({});
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setEditedCandidate(prev => prev ? {
      ...prev,
      [name]: value
    } : null);
  };

  const handleQuestionTemplateSelect = (templateId: number): void => {
    setEditedCandidate(prev => prev ? {
      ...prev,
      question_template_id: templateId
    } : null);
    setValidationErrors(prev => {
      const { question_template_id, ...rest } = prev;
      return rest;
    });
  };

  /**
   * Validate candidate form data
   * @param candidate - Candidate data
   * @returns Validation errors
   */
  const validateCandidateForm = (candidate: CandidateFormData): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!candidate.name || !candidate.name.trim()) {
      errors.name = '氏名を入力してください。';
    } else if (containsEmoji(candidate.name)) {
      errors.name = '絵文字は使用できません。';
    }

    if (!candidate.email || !candidate.email.trim()) {
      errors.email = 'メールアドレスを入力してください。';
    } else if (!validateEmail(candidate.email)) {
      errors.email = '不正なメールアドレスです。';
    } else if (containsEmoji(candidate.email)) {
      errors.email = '絵文字は使用できません。';
    }

    if (candidate.assignment_location && containsEmoji(candidate.assignment_location)) {
      errors.assignment_location = '絵文字は使用できません。';
    }

    if (!candidate.question_template_id || candidate.question_template_id === 0) {
      errors.question_template_id = '質問テンプレートを選択してください。';
    }

    if (candidate.internal_memo && containsEmoji(candidate.internal_memo)) {
      errors.internal_memo = '絵文字は使用できません。';
    }

    return errors;
  };

  const saveCandidate = async (): Promise<void> => {
    if (!editedCandidate) return;

    const errors = validateCandidateForm(editedCandidate);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const candidateData = {
        ...editedCandidate,
      };

      await axios.put(`/candidates/${editedCandidate.id}`, candidateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCandidates();
      setSelectedCandidate(editedCandidate);
      setIsEditMode(false);
      setEditedCandidate(null);
      setValidationErrors({});

      alert(`${editedCandidate.name}さんのリファレンスチェックを変更しました。`);
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error updating candidate:', error);

      if (axiosError.response?.status === 422 && axiosError.response.data.errors) {
        const responseErrors = axiosError.response.data.errors;
        const formattedErrors: ValidationErrors = {};

        if (responseErrors.email) {
          formattedErrors.email = responseErrors.email[0];
        }

        setValidationErrors(formattedErrors);
      } else {
        alert('候補者情報の更新に失敗しました。');
      }
    }
  };

  const handleShowDeleteModal = (candidate: Candidate): void => {
    setSelectedCandidate({
      ...candidate,
    });

    if (window.confirm(`${candidate.name}さんのリファレンスチェックを削除しますか？`)) {
      deleteCandidate();
    }
  };

  const deleteCandidate = async (): Promise<void> => {
    if (!selectedCandidate) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/candidates/${selectedCandidate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCandidates();
      setShowDetailModal(false); // Close the detail modal after successful deletion

      alert(`${selectedCandidate.name}さんのリファレンスチェックを削除しました。`);
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error deleting candidate:', error);
    }
  };

  const handleShowEmailModal = (candidate: Candidate, remindMail: boolean): void => {
    setSelectedCandidate({
      ...candidate
    });

    setShowEmailModal(true);
  };

  const sendCandidateEmail = async (emailAddress: string, urlToken: string): Promise<boolean> => {
    if (!selectedCandidate) return false;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/candidates/${selectedCandidate.id}/send-email`, {
        email: emailAddress,
        token: urlToken
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCandidates();
      setShowEmailModal(false);
      alert('メール送信に成功しました。');
      return true;
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      alert('メール送信に失敗しました。管理者にお問い合わせください。');
      console.error('Error sending email:', error);
      return false;
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const paginate = (pageNumber: number): void => setCurrentPage(pageNumber);

  const pendingCount = candidates.status_counts?.pending_count || 0;
  const inProgressCount = candidates.status_counts?.in_progress_count || 0;
  const completedCount = candidates.status_counts?.completed_count || 0;
  const totalCount = pendingCount + inProgressCount + completedCount;

  return (
    <div className="dashboard-container">
      <Header user={user} />

      <Container>
        <div className="search-container">
          <div className="status-filter-container">
            <div 
              className={`status-filter-label ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabChange('all')}
            >
              全て <span className="status-count">({totalCount})</span>
            </div>
            <div 
              className={`status-filter-label ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => handleTabChange('pending')}
            >
              未案内 <span className="status-count">({pendingCount})</span>
            </div>
            <div 
              className={`status-filter-label ${activeTab === 'in-progress' ? 'active' : ''}`}
              onClick={() => handleTabChange('in-progress')}
            >
              進行中 <span className="status-count">({inProgressCount})</span>
            </div>
            <div 
              className={`status-filter-label ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => handleTabChange('completed')}
            >
              完了 <span className="status-count">({completedCount})</span>
            </div>
          </div>

          <div className="search-input-container">
            <Form>
            <Form.Control
              type="text"
              placeholder="氏名／メールアドレス"
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
            </Form>
          </div>

          <div className="create-button-container">
            <Button 
              variant="success" 
              className="create-button"
              onClick={() => navigate('/candidate/new')}
            >
              新規候補者を作成
            </Button>
          </div>
        </div>

        <div className='reference-check-text'>
          リファレンスチェック進行状況
        </div>

        <div className="candidates-table-container">
          <table className="candidates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  氏名 {getSortIcon('name', sortConfig)}
                </th>
                <th onClick={() => handleSort('status')} className="sortable text-center">
                  ステータス {getSortIcon('status', sortConfig)}
                </th>
                <th onClick={() => handleSort('email_sent')} className="sortable text-center">
                  候補者への案内 {getSortIcon('email_sent', sortConfig)}
                </th>
                <th onClick={() => handleSort('response_count')} className="sortable text-center">
                  推薦の取得 {getSortIcon('response_count', sortConfig)}
                </th>
                <th onClick={() => handleSort('reference_check')} className="sortable text-center">
                  リファレンスチェック {getSortIcon('reference_check', sortConfig)}
                </th>
                <th className="text-center">
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  <div className="blank-form-container">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">情報を読み込んでいます...</p>
                  </div>
                </td>
              </tr>
              ) : (
              candidates.data?.length > 0 ? (
                candidates.data.map(candidate => (
                  <tr key={candidate.id}>
                    <td className="text-left">{candidate.name}</td>
                    <td className="text-center">
                      <div className={`status-badge ${STATUS_CLASS[candidate.status as StatusType]}`}>
                        {STATUS_TEXT[candidate.status as StatusType]}
                      </div>
                    </td>
                    <td className="text-center">
                      {candidate.email_sent ? (
                        <FontAwesomeIcon icon={faCheck} className="checkmark" />
                      ) : (
                        <Button 
                          className="guide-button"
                          onClick={() => handleShowEmailModal(candidate, false)}
                        >
                          案内する
                        </Button>
                      )}
                    </td>
                    <td className="text-center">
                      {!candidate.email_sent ? (
                        ''
                      ) : candidate.response_count >= 2 ? (
                        <FontAwesomeIcon icon={faCheck} className="checkmark" />
                      ) : (
                        <div 
                          className="response-count"
                          onClick={() => handleShowEmailModal(candidate, true)}
                          style={{ cursor: 'pointer' }}
                        >
                          残り{2 - (candidate.response_count || 0)}件
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      {(candidate.status === STATUS.COMPLETED || (candidate.status === STATUS.IN_PROGRESS && candidate.response_count >= 1)) ? (
                        <div 
                          className="result-badge"
                          onClick={() => navigate(`/results/${candidate.id}`)}
                        >
                          結果
                        </div>
                      ) : (
                        <div className="waiting-result">
                          結果待ち
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <Button 
                        className="detail-button"
                        onClick={() => viewCandidateDetails(candidate)}
                      >
                        詳細
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center">該当するデータはありません。</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {candidates.last_page > 1 && renderPagination(currentPage, candidates.last_page, paginate)}
      </Container>

      {/* 詳細モーダル */}
      <Modal
        show={showDetailModal} 
        onHide={() => setShowDetailModal(false)}
        centered
        className="candidate-detail-modal"
        backdrop="static"  // Prevent close when clicking outside
        keyboard={false}   // Optional: Prevent closing with ESC key
      >
        <Modal.Header closeButton>
          <Modal.Title>候補者詳細</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCandidate && !isEditMode && (
            <div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">氏名</div>
                <div className="candidate-detail-value">{selectedCandidate.name}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">メールアドレス</div>
                <div className="candidate-detail-value">{selectedCandidate.email}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職種</div>
                <div className="candidate-detail-value">{selectedCandidate.job_type ? getJobTypeString(Number(selectedCandidate.job_type)) : '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職位</div>
                <div className="candidate-detail-value">{selectedCandidate.position ? getPositionString(Number(selectedCandidate.position)) : '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">配属先</div>
                <div className="candidate-detail-value">{selectedCandidate.assignment_location || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">質問選択</div>
                <div className="candidate-detail-value">
                  {questionTemplates.find(t => t.id == selectedCandidate.question_template_id)?.name || '-'}
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">社内向けメモ</div>
                <div className="candidate-detail-value internal-memo">{selectedCandidate.internal_memo || '-'}</div>
              </div>

              {(selectedCandidate.status === STATUS.COMPLETED || (selectedCandidate.status === STATUS.IN_PROGRESS && selectedCandidate.response_count >= 1)) && (
                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">リファレンスチェック</div>
                  <div className="candidate-detail-value">
                    <Button 
                      className="result-button"
                      onClick={() => navigate(`/results/${selectedCandidate.id}`)}
                    >
                      結果
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isEditMode && editedCandidate && (
            <div className='candidate-edit-panel'>
              <Form>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">氏名 <span className="required-mark">*</span></div>
                <div className="candidate-detail-value">
                  <Form.Control
                    type="text"
                    name="name"
                    value={editedCandidate.name}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.name}
                    required
                  />
                  {validationErrors.name && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.name}
                    </Form.Control.Feedback>
                  )}
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">メールアドレス <span className="required-mark">*</span></div>
                <div className="candidate-detail-value">
                  <Form.Control
                    type="text"
                    name="email"
                    value={editedCandidate.email}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.email}
                    required
                  />
                  {validationErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.email}
                    </Form.Control.Feedback>
                  )}
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職種</div>
                <div className="candidate-detail-value">
                  <Form.Select
                    name="job_type"
                    value={editedCandidate.job_type || ''}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">選択してください</option>
                    {JOB_TYPE_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職位</div>
                <div className="candidate-detail-value">
                  <Form.Select
                    name="position"
                    value={editedCandidate.position || ''}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">選択してください</option>
                    {POSITION_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">配属先</div>
                <div className="candidate-detail-value">
                  <Form.Control
                    type="text"
                    name="assignment_location"
                    value={editedCandidate.assignment_location || ''}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.assignment_location}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.assignment_location}
                  </Form.Control.Feedback>
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">質問選択 <span className="required-mark">*</span></div>
                <div className="candidate-detail-value">
                  <div className="question-template-selection">
                    <Button
                      variant="outline-secondary"
                      className="question-template-button"
                      onClick={() => setShowQuestionTemplateModal(true)}
                      style={{ 
                        border: validationErrors.question_template_id ? '1px solid #dc3545' : '1px solid #ced4da'
                      }}
                    >
                      {editedCandidate.question_template_id 
                        ? questionTemplates.find(t => t.id === Number(editedCandidate.question_template_id))?.name || '選択してください'
                        : '選択してください'
                      }
                    </Button>
                    {validationErrors.question_template_id && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.question_template_id}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">社内向けメモ</div>
                <div className="candidate-detail-value">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="internal_memo"
                    value={editedCandidate.internal_memo || ''}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.internal_memo}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.internal_memo}
                  </Form.Control.Feedback>
                </div>
              </div>
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!isEditMode ? (
            <>
              <Button 
                variant="success" 
                className="edit-button"
                onClick={handleEditMode}
              >
                編集
              </Button>
              <Button 
                variant="danger" 
                className="delete-button"
                onClick={() => selectedCandidate && handleShowDeleteModal(selectedCandidate)}
              >
                削除
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="success" 
                className="save-button"
                onClick={saveCandidate}
              >
                保存
              </Button>
              <Button 
                variant="secondary" 
                className="cancel-button"
                onClick={handleCancelEdit}
              >
                キャンセル
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* 削除確認モーダルは標準のconfirm()関数に置き換えられました */}

      {/* 案内を送るモーダル */}
      <SendGuideModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        candidate={selectedCandidate}
        onSend={sendCandidateEmail}
      />

      {/* Question Template Selection Modal */}
      <QuestionTemplateSelectionModal
        show={showQuestionTemplateModal}
        onHide={() => setShowQuestionTemplateModal(false)}
        questionTemplates={questionTemplates}
        selectedTemplateId={editedCandidate?.question_template_id || 0}
        onSelect={handleQuestionTemplateSelect}
      />
    </div>
  );
};

export default CandidateList;
